import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import { useState } from 'react';

import { GroupRole } from '@/__generated__/graphql';
import Breadcrumbs from '@/components/Breadcrumbs';
import Button from '@/components/Button';
import CircularProgress from '@/components/CircularProgress/CircularProgress';
import CountBox from '@/components/CountBox';
import Dialog from '@/components/Dialog';
import Divider from '@/components/Divider';
import GroupMember from '@/components/GroupMember';
import FAIcon from '@/components/Icon/FAIcon';
import Link from '@/components/Link';
import MemberRequest from '@/components/MemberRequests/Group';
import Pagination from '@/components/Pagination/Pagination';
import Tooltip from '@/componentsv2/Tooltip';
import { primary } from '@/theme/colors';

import { ActivityGuardModal } from './ActivityGuardModal';
import AddTeamMemberDialog from './AddTeamMemberDialog';
import { TransferOwnershipDialog } from './TransferOwnershipDialog';
import { extractActivityGuardError, useTeamMembers } from './useTeamMembers';

const useStyles = makeStyles((theme) => ({
  root: {},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  headerLink: {
    whiteSpace: 'nowrap',
    marginLeft: 15,
    fontSize: 14,
  },
  members: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  membersActions: {
    width: 200,
  },
  membersActionsTitle: {
    color: 'var(--darkGray)',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  membersList: {
    flex: 1,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 20,
    borderTop: '1px solid var(--borderColor)',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 25,
    '& span': {
      paddingTop: 15,
    },
  },
  counts: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 24,
    // @ts-expect-error TS(2339) FIXME: Property 'breakpoints' does not exist on type 'Def... Remove this comment to see the full error message
    [theme.breakpoints.down('md')]: {
      gap: 16,
      '& > *': {
        width: 170,
      },
    },
  },
  search: {
    marginLeft: 'auto',
    // @ts-expect-error TS(2339) FIXME: Property 'breakpoints' does not exist on type 'Def... Remove this comment to see the full error message
    [theme.breakpoints.down('md')]: {
      marginTop: 10,
      width: 190,
    },
  },
}));

/* eslint-disable react/prop-types */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function TeamSMB({ viewer, groupId, team, notify, fetchGroup }: any) {
  const s = useStyles();
  const { name, stats = {}, html_url: htmlUrl } = team;

  const hook = useTeamMembers({ viewer, groupId, team, notify, fetchGroup });

  const [selfDemoteConfirm, setSelfDemoteConfirm] = useState(false);

  const isOverCapacity =
    hook.seatAllocation && hook.seatAllocation.used > hook.seatAllocation.included;
  const noSeatsAvailable = hook.seatAllocation && hook.seatAllocation.available <= 0;

  const handleAddClose = () => hook.setAddOpen(false);

  const handleUpdateWithSelfDemotion = async (id: string, role: GroupRole) => {
    // Self-demotion to viewer: show confirmation
    const memberNode = hook.members.edges.find(({ node: m }: any) => m.id === id)?.node;
    if (role === GroupRole.Viewer && memberNode?.user?.id === viewer.id) {
      setSelfDemoteConfirm(true);
      return;
    }
    await hook.updateTeamMemberAndFetch(id, role);
  };

  const handleSelfDemoteConfirm = async () => {
    const viewerNode = hook.members.edges.find(
      ({ node: m }: any) => m.user?.id === viewer.id
    )?.node;
    if (!viewerNode) return;
    setSelfDemoteConfirm(false);
    try {
      await hook.updateTeamMember({ variables: { id: viewerNode.id, role: GroupRole.Viewer } });
      hook.refreshSeatAllocation();
    } catch (err: any) {
      const guard = extractActivityGuardError(err);
      if (guard) {
        hook.setActivityGuardError(guard);
      } else {
        notify(err?.graphQLErrors?.[0]?.message || 'Failed to update role', 'error');
      }
    }
  };

  return (
    <div className={s.root}>
      <div className={s.header}>
        <Breadcrumbs crumbs={[{ title: name, href: htmlUrl }]} />
        {team.internal_network && (
          <Link className={s.headerLink} to={`/search?networks[]=${team.internal_network.id}`}>
            <FAIcon icon="users-class" /> View Network
          </Link>
        )}
        <Link className={s.headerLink} to={`${htmlUrl}/settings`}>
          <FAIcon icon="cog" /> Settings &amp; Billing
        </Link>
        {viewer.groups?.length > 1 && (
          <Link className={s.headerLink} to="/teams">
            <FAIcon icon="exchange" /> View All Teams
          </Link>
        )}
      </div>

      {hook.awaitingMembersData && (
        <MemberRequest
          onApprove={hook.fetchPageMembers}
          memberRequests={hook.awaitingMembersData}
        />
      )}

      {isOverCapacity && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your team has more members than available seats. Add seats or change some members to
          Viewers.{' '}
          <Link to={`${htmlUrl}/settings/subscription`} style={{ fontWeight: 600 }}>
            Manage Subscription
          </Link>
        </Alert>
      )}

      <div className={s.counts}>
        <CountBox count={hook.totalMembers} label="Team Members" />
        <CountBox count={stats.expert_request_count} label="Expert Requests" />
        <CountBox count={stats.consultation_count} label="Consultations" />
        <CountBox
          count={`${hook.seatAllocation?.used ?? 0} of ${hook.seatAllocation?.included ?? 0}`}
          linkTo={`${htmlUrl}/settings/subscription`}
          label={`${hook.seatAllocation?.included === 1 ? 'Seat' : 'Seats'} Allocated`}
          warn={!!noSeatsAvailable}
        />
        <div className={s.search}>
          <TextField
            value={hook.nameSearch ?? ''}
            margin="none"
            fullWidth={false}
            onChange={({ target: { value } }) => {
              hook.refetch({});
              hook.setPage(1);
              hook.setNameSearch(hook.normalizeSpace(value));
            }}
            placeholder="Filter by name"
          />
        </div>
      </div>
      <Divider />

      <div className={s.members}>
        <div className={s.membersActions}>
          <div className={s.membersActionsTitle}>Team Members</div>
          {!hook.perms.add ? (
            <Tooltip title="You do not have permission to add members.">
              <span>
                <Button color="white" variant="text" fontColor={primary} size="small" disabled>
                  Add
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              onClick={() => hook.setAddOpen(true)}
              color="white"
              variant="text"
              fontColor={primary}
              size="small"
            >
              Add
            </Button>
          )}
          <AddTeamMemberDialog
            groupId={groupId}
            userFilter={hook.addUserFilter}
            open={hook.addOpen}
            onSubmit={() => {
              hook.setAddTeamMemberLoading(true);
              handleAddClose();
            }}
            onClose={handleAddClose}
            onRequestDone={() => {
              hook.setAddTeamMemberLoading(false);
              hook.fetchPageMembers();
              hook.refreshSeatAllocation();
            }}
            addTeamMember={hook.addTeamMember}
            inviteUser={hook.inviteUser}
            isSMB
            seatsAvailable={hook.seatAllocation?.available ?? 0}
            settingsUrl={htmlUrl}
          />
        </div>

        <div className={s.membersList}>
          {hook.isLoading ? (
            <div className={s.loading}>
              <CircularProgress size={52} />
              <span>Loading...</span>
            </div>
          ) : (
            hook.members.edges.map(({ node: m }: { node: any }) => (
              <GroupMember
                key={m.id}
                member={m}
                groupId={groupId}
                removeGroupMember={hook.canRemoveMember(m) ? hook.removeTeamMemberAndFetch : null}
                updateGroupMember={
                  hook.perms.update[m.role as keyof typeof hook.perms.update]
                    ? handleUpdateWithSelfDemotion
                    : null
                }
                allowedRoles={hook.perms.allowedRoles
                  .filter((r: string) => r !== 'admin')
                  .filter((r: string) => m.user || r !== 'owner')}
                disabledRoles={noSeatsAvailable && m.role === 'viewer' ? ['member'] : []}
              />
            ))
          )}
          {hook.totalPages > 1 && (
            <div className={s.pagination}>
              <Pagination
                page={hook.page}
                count={hook.totalPages}
                onChange={(_: any, value: any) => {
                  hook.refetch({ page: value });
                  hook.setPage(value);
                }}
              />
            </div>
          )}
          {noSeatsAvailable && (
            <div style={{ marginTop: 42, fontSize: 14, color: 'var(--darkGray)' }}>
              Add seats in{' '}
              <Link to={`${htmlUrl}/settings`} className="hover:underline">
                Settings & Billing
              </Link>
              .
            </div>
          )}
        </div>
      </div>

      <ActivityGuardModal
        open={!!hook.activityGuardError}
        onClose={() => hook.setActivityGuardError(null)}
        error={hook.activityGuardError}
      />

      <TransferOwnershipDialog
        open={!!hook.transferTarget}
        onClose={() => hook.setTransferTarget(null)}
        onConfirm={hook.handleTransferConfirm}
        submitting={hook.transferLoading}
        targetMember={hook.transferTarget}
        currentOwner={hook.currentOwner}
      />

      <Dialog
        open={selfDemoteConfirm}
        title="Demote yourself to Viewer?"
        onConfirm={handleSelfDemoteConfirm}
        onCancel={() => setSelfDemoteConfirm(false)}
        confirmLabel="Demote to Viewer"
        confirmButtonProps={{ color: 'red' }}
      >
        <p>
          You&apos;ll lose the ability to create expert requests and book consultations. You can ask
          the team owner to restore your Member seat later.
        </p>
      </Dialog>
    </div>
  );
}
